import type { TournamentMatch } from "@mixitone/models";
import { getMatchEntryIds } from "tournament-state/tournamentMatchEligibility";

export const RECENT_RESULTS_LIMIT = 5;

export interface CompetitionOverviewCounts {
  competitionId: string;
  total: number;
  scored: number;
  voided: number;
  awaitingScore: number;
  inProgress: number;
  remaining: number;
  completionPercent: number;
}

export interface RoundGroupOverview {
  key: string;
  label: string;
  round: number;
  group?: string;
  total: number;
  scored: number;
  voided: number;
  remaining: number;
}

export interface CompetitionOverviewDetail extends CompetitionOverviewCounts {
  roundGroups: RoundGroupOverview[];
}

/** Match counts for one stage (main or playoffs). */
export interface PhaseOverviewCounts {
  total: number;
  scored: number;
  voided: number;
  awaitingScore: number;
  inProgress: number;
  remaining: number;
  completionPercent: number;
}

/**
 * Current tournament stage when a separate playoff phase exists.
 * - main: main-stage matches still unresolved
 * - between: main stage complete, playoffs not started (announcement window)
 * - playoffs: at least one playoff match has started or finished
 */
export type GamesOverviewPhase = "main" | "between" | "playoffs";

export interface GamesOverviewPhases {
  hasPlayoffs: boolean;
  current: GamesOverviewPhase | null;
  main: PhaseOverviewCounts;
  playoffs: PhaseOverviewCounts;
}

export interface GamesOverview {
  total: number;
  scored: number;
  voided: number;
  awaitingScore: number;
  inProgress: number;
  remaining: number;
  completionPercent: number;
  phases: GamesOverviewPhases;
  openConflictCount: number;
  attentionCount: number;
  competitions: CompetitionOverviewCounts[];
  competitionDetails: CompetitionOverviewDetail[];
  awaitingScoreMatches: TournamentMatch[];
  recentResults: TournamentMatch[];
}

type PlayoffMode = "phase" | "division";

function emptyCompetitionCounts(competitionId: string): CompetitionOverviewCounts {
  return {
    competitionId,
    total: 0,
    scored: 0,
    voided: 0,
    awaitingScore: 0,
    inProgress: 0,
    remaining: 0,
    completionPercent: 0,
  };
}

function emptyPhaseCounts(): PhaseOverviewCounts {
  return {
    total: 0,
    scored: 0,
    voided: 0,
    awaitingScore: 0,
    inProgress: 0,
    remaining: 0,
    completionPercent: 0,
  };
}

function isAwaitingScore(match: TournamentMatch): boolean {
  return Boolean(match.needsWinners && getMatchEntryIds(match).length >= 2);
}

function isScored(match: TournamentMatch): boolean {
  return Boolean(match.isComplete && (match.entries?.winners.length ?? 0) > 0);
}

function completionPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

function compareCompletedAtAsc(a: TournamentMatch, b: TournamentMatch): number {
  const ta = a.completed_at?.getTime() ?? 0;
  const tb = b.completed_at?.getTime() ?? 0;
  if (ta !== tb) return ta - tb;
  const ra = a.round ?? 0;
  const rb = b.round ?? 0;
  if (ra !== rb) return ra - rb;
  const ia = a.match_index ?? 0;
  const ib = b.match_index ?? 0;
  if (ia !== ib) return ia - ib;
  return String(a.id ?? "").localeCompare(String(b.id ?? ""));
}

function roundGroupKey(round: number, group?: string): string {
  return group ? `r${round}:g${group}` : `r${round}`;
}

function roundGroupLabel(round: number, group?: string): string {
  const roundLabel = `Round ${round + 1}`;
  return group ? `${roundLabel} / ${group}` : roundLabel;
}

function hasDivision(match: TournamentMatch): boolean {
  return match.format_data?.division != null;
}

/**
 * Detect how playoffs are marked for a competition.
 * Prefer explicit format_data.phase === "playoff". Fall back to Swiss-style
 * undivided main stage + division-tagged knockout matches.
 */
export function detectPlayoffMode(matches: Iterable<TournamentMatch>): PlayoffMode | null {
  let sawExplicitPlayoff = false;
  let sawWithDivision = false;
  let sawWithoutDivision = false;

  for (const match of matches) {
    if (match.format_data?.phase === "playoff") {
      sawExplicitPlayoff = true;
      break;
    }
    if (hasDivision(match)) sawWithDivision = true;
    else sawWithoutDivision = true;
  }

  if (sawExplicitPlayoff) return "phase";
  if (sawWithDivision && sawWithoutDivision) return "division";
  return null;
}

export function isPlayoffMatch(match: TournamentMatch, mode: PlayoffMode | null): boolean {
  if (mode === "phase") return match.format_data?.phase === "playoff";
  if (mode === "division") return hasDivision(match);
  return false;
}

function finalizePhaseCounts(counts: PhaseOverviewCounts): PhaseOverviewCounts {
  return {
    ...counts,
    completionPercent: completionPercent(counts.scored + counts.voided, counts.total),
  };
}

function resolveCurrentPhase(main: PhaseOverviewCounts, playoffs: PhaseOverviewCounts): GamesOverviewPhase {
  const mainComplete = main.total === 0 || main.scored + main.voided === main.total;
  const playoffsStarted =
    playoffs.inProgress + playoffs.awaitingScore + playoffs.scored + playoffs.voided > 0;

  if (!mainComplete) return "main";
  if (!playoffsStarted) return "between";
  return "playoffs";
}

/**
 * Derive Games-screen overview metrics from projected matches.
 * Remaining matches are pending/ready (not started). Scored matches have winners.
 * Voided matches count as completed progress but not as scored results.
 * When a format has a separate playoff phase, `phases` splits main vs playoff progress.
 */
export function buildGamesOverview(
  matches: Iterable<TournamentMatch>,
  options: {
    competitionIds?: string[];
    openConflictCount?: number;
    recentResultsLimit?: number;
  } = {},
): GamesOverview {
  const openConflictCount = options.openConflictCount ?? 0;
  const recentResultsLimit = options.recentResultsLimit ?? RECENT_RESULTS_LIMIT;
  const competitionMap = new Map<string, CompetitionOverviewCounts>();
  const roundGroupMap = new Map<string, Map<string, RoundGroupOverview>>();
  const matchesByCompetition = new Map<string, TournamentMatch[]>();

  for (const competitionId of options.competitionIds ?? []) {
    competitionMap.set(competitionId, emptyCompetitionCounts(competitionId));
    roundGroupMap.set(competitionId, new Map());
    matchesByCompetition.set(competitionId, []);
  }

  const listed: TournamentMatch[] = [];
  for (const match of matches) {
    const competitionId = match.tournament_competition_id;
    if (!competitionId) continue;
    listed.push(match);
    let bucket = matchesByCompetition.get(competitionId);
    if (!bucket) {
      bucket = [];
      matchesByCompetition.set(competitionId, bucket);
    }
    bucket.push(match);
  }

  const playoffModeByCompetition = new Map<string, PlayoffMode | null>();
  for (const [competitionId, competitionMatches] of matchesByCompetition) {
    playoffModeByCompetition.set(competitionId, detectPlayoffMode(competitionMatches));
  }

  const awaitingScoreMatches: TournamentMatch[] = [];
  const scoredMatches: TournamentMatch[] = [];
  let total = 0;
  let scored = 0;
  let voided = 0;
  let awaitingScore = 0;
  let inProgress = 0;
  let remaining = 0;
  const main = emptyPhaseCounts();
  const playoffs = emptyPhaseCounts();
  let hasPlayoffs = false;

  for (const match of listed) {
    const competitionId = match.tournament_competition_id!;
    total += 1;
    let counts = competitionMap.get(competitionId);
    if (!counts) {
      counts = emptyCompetitionCounts(competitionId);
      competitionMap.set(competitionId, counts);
      roundGroupMap.set(competitionId, new Map());
    }
    counts.total += 1;

    const round = match.round ?? 0;
    const group = typeof match.format_data?.group === "string" ? match.format_data.group : undefined;
    const groupKey = roundGroupKey(round, group);
    const groups = roundGroupMap.get(competitionId)!;
    let roundGroup = groups.get(groupKey);
    if (!roundGroup) {
      roundGroup = {
        key: groupKey,
        label: roundGroupLabel(round, group),
        round,
        group,
        total: 0,
        scored: 0,
        voided: 0,
        remaining: 0,
      };
      groups.set(groupKey, roundGroup);
    }
    roundGroup.total += 1;

    const playoffMode = playoffModeByCompetition.get(competitionId) ?? null;
    const isPlayoff = isPlayoffMatch(match, playoffMode);
    if (playoffMode) hasPlayoffs = true;
    const phaseCounts = isPlayoff ? playoffs : main;
    phaseCounts.total += 1;

    if (isAwaitingScore(match)) {
      awaitingScore += 1;
      counts.awaitingScore += 1;
      phaseCounts.awaitingScore += 1;
      awaitingScoreMatches.push(match);
    } else if (isScored(match)) {
      scored += 1;
      counts.scored += 1;
      phaseCounts.scored += 1;
      roundGroup.scored += 1;
      scoredMatches.push(match);
    } else if (match.isVoided || match.status === "voided") {
      voided += 1;
      counts.voided += 1;
      phaseCounts.voided += 1;
      roundGroup.voided += 1;
    } else if (match.isInProgress) {
      inProgress += 1;
      counts.inProgress += 1;
      phaseCounts.inProgress += 1;
    } else if (match.isPending || match.isReady) {
      remaining += 1;
      counts.remaining += 1;
      phaseCounts.remaining += 1;
      roundGroup.remaining += 1;
    }
  }

  awaitingScoreMatches.sort(compareCompletedAtAsc);
  scoredMatches.sort((a, b) => -compareCompletedAtAsc(a, b));

  const competitions = [...competitionMap.values()].map((counts) => ({
    ...counts,
    completionPercent: completionPercent(counts.scored + counts.voided, counts.total),
  }));

  const competitionDetails: CompetitionOverviewDetail[] = competitions.map((counts) => {
    const groups = roundGroupMap.get(counts.competitionId);
    const roundGroups = groups
      ? [...groups.values()].toSorted((a, b) => {
          if (a.round !== b.round) return a.round - b.round;
          return String(a.group ?? "").localeCompare(String(b.group ?? ""));
        })
      : [];
    return { ...counts, roundGroups };
  });

  const mainFinal = finalizePhaseCounts(main);
  const playoffsFinal = finalizePhaseCounts(playoffs);

  return {
    total,
    scored,
    voided,
    awaitingScore,
    inProgress,
    remaining,
    completionPercent: completionPercent(scored + voided, total),
    phases: {
      hasPlayoffs,
      current: hasPlayoffs ? resolveCurrentPhase(mainFinal, playoffsFinal) : null,
      main: mainFinal,
      playoffs: playoffsFinal,
    },
    openConflictCount,
    attentionCount: awaitingScore + openConflictCount,
    competitions,
    competitionDetails,
    awaitingScoreMatches,
    recentResults: scoredMatches.slice(0, recentResultsLimit),
  };
}

export function pendingMatchCountFromOverview(overview: GamesOverview): Record<string, number> {
  const pending: Record<string, number> = {};
  for (const competition of overview.competitions) {
    pending[competition.competitionId] = competition.remaining;
  }
  return pending;
}
